import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import {
  intakeQuestionnaireActions,
  onboardingQuestionnaireActions,
} from '../../../../../../store';
import { QUESTIONNAIRE_TYPES } from '../constants';
import { useProviderQuestionnaireState } from './useProviderQuestionnaireState';

import type { AppDispatch } from '../../../../../../store';
import type { SelectValueType } from '../../../../../../utils/types';

export function useProviderQuestionnaireActions(providerId: number) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { loading, onboardingQuestionnaires, intakeQuestionnaires } =
    useProviderQuestionnaireState();

  useEffect(() => {
    const onboardingRequest = dispatch(
      onboardingQuestionnaireActions.getOnboardingQuestionnaires({
        params: { provider_id: providerId },
        getAccessTokenSilently,
      }),
    );
    const intakeRequest = dispatch(
      intakeQuestionnaireActions.getIntakeQuestionnaires({
        params: { provider_id: providerId },
        getAccessTokenSilently,
      }),
    );

    return () => {
      onboardingRequest.abort();
      intakeRequest.abort();
      dispatch(onboardingQuestionnaireActions.clearOnboardingQuestionnaires());
      dispatch(intakeQuestionnaireActions.clearIntakeQuestionnaires());
    };
  }, [dispatch, getAccessTokenSilently, providerId]);

  const sendQuestionnaire = (
    selectedQuestionnaire: SelectValueType,
    selectedClientId: SelectValueType,
  ) => {
    if (!selectedQuestionnaire || !selectedClientId) return;

    const data = { provider_id: providerId, client_id: Number(selectedClientId) };

    if (selectedQuestionnaire === QUESTIONNAIRE_TYPES.INTAKE) {
      dispatch(
        intakeQuestionnaireActions.sendIntakeQuestionnaire({ getAccessTokenSilently, data }),
      );
      return;
    }

    dispatch(
      onboardingQuestionnaireActions.sendOnboardingQuestionnaire({ getAccessTokenSilently, data }),
    );
  };

  return {
    loading,
    onboardingQuestionnaires,
    intakeQuestionnaires,
    sendQuestionnaire,
  };
}
